import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Dato } from '../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale, Riquadro, Scelta, Tabella, type TonoEtichetta } from '../componenti/base.tsx'
import { IconaCarica, IconaScarica } from '../componenti/Icone.tsx'
import { DatoDerivato, GruppoScelta, SpuntaObbligatoria, useFuocoSulPrimoErrore } from '../componenti/persona-campi.tsx'
import { caricaDocumento } from '../servizi/documenti.ts'
import { ETICHETTA_IN_VERIFICA, attesaAntivirus } from '../servizi/regole-documenti.ts'
import { nessunErrore, oggiLocale, type Errori, type SchedaLetta } from '../servizi/regole-anagrafica.ts'
import {
  ETICHETTE_ESITO, FIRMATARI, PASSI_COMPLETAMENTO, TIPO_DOCUMENTO_FIRMATO, completamentoNuovo, corpoGiustificativo,
  corpoModulo, corpoRevoca, corpoSottoscrizione, descriviDaCollegare, descriviFirma, erroriDaApi, firmaDi, firmaDiTerzi,
  giornoItaliano, moduloNuovo, nomeFirmatario, nomeStatoConsenso, nomeTipoConsenso, passoDellErrore, revocaNuova,
  sottoscrizioneNuova, spiegazioneGiustificativo, statoModuloFirmato, termineFirma, tipiGiustificativo, tipiModulo,
  verificaModulo, verificaPassoCompletamento, verificaRevoca, verificaSottoscrizione,
  type ConsensiLetti, type ConsensoLetto, type Esito, type EsitoVigente, type FirmaDaCompletare, type ModuloConsensi,
  type ModuloFirmato, type StatoCompletamento, type StatoModulo, type StatoRevoca, type StatoSottoscrizione,
  type TipoGiustificativo, type TipoModulo, type VoceVigente,
} from '../servizi/regole-consensi.ts'

/**
 * Consensi della Scheda utente Rev05 (D-08, D-09): indicatore dei consensi necessari per registrare
 * interventi, esito vigente delle quattro dichiarazioni, registro completo, registrazione di una
 * sottoscrizione, revoca e collegamento della scansione del modulo firmato.
 *
 * Quando firma un genitore, il tutore, l'amministratore di sostegno (454) o il responsabile della
 * struttura di accoglienza (456) si registra il nome di chi firma e serve il documento che ne prova
 * il potere di firma. Se manca, i consensi si registrano lo stesso e la firma resta «da completare»:
 * l'avviso della sezione la elenca con il termine (456: trenta giorni dalla registrazione, salvo
 * impostazione), e il comando «Completa» apre un percorso guidato in tre passi (documento, tipo,
 * conferma). Scaduto il termine ne viene avvisata anche la Direzione; gli interventi non si bloccano.
 *
 * Il modulo firmato e' un foglio solo con tutte le dichiarazioni (osservazioni n. 4 del 23/10/2026,
 * punto 12): «Carica il modulo firmato» lo carica una volta, oppure lo sceglie fra i documenti della
 * scheda di un tipo che vale come modulo, e lo collega a tutte le dichiarazioni in vigore che non lo
 * hanno. Gli esiti restano quelli registrati, e il modulo resta facoltativo (D-09).
 *
 * Nessuna scelta e' preselezionata: un consenso non si presume, e il diniego si registra come esito
 * proprio, distinto dal consenso non raccolto. Le decisioni restano dell'API (rotte/consensi.ts):
 * i comandi seguono i permessi della scheda solo per non offrire operazioni che verrebbero rifiutate.
 */

type Ricarica = () => Promise<void>

const TONO_ESITO: Record<EsitoVigente, TonoEtichetta> = {
  espresso: 'positivo', negato: 'critico', revocato: 'critico', mancante: 'attenzione',
}

const OPZIONI_ESITO = [{ valore: 'espresso', etichetta: 'Esprimo' }, { valore: 'negato', etichetta: 'Non esprimo' }]

/**
 * Messaggio in testa a una finestra. Un errore che non viene dall'API e' la rete o l'archivio dei
 * documenti che non rispondono: a schermo va una frase, non il messaggio tecnico del browser.
 */
const messaggioDi = (e: unknown) => (e instanceof ErroreApi
  ? e.stato === 404 ? 'L’operazione non rientra negli incarichi in corso su questa scheda.' : e.message
  : 'Non è andata a buon fine: il collegamento con il server o con l’archivio dei documenti si è interrotto. Riprova tra poco.')

type TipoDocumentoLetto = TipoGiustificativo & TipoModulo

let tipiInCorso: Promise<TipoDocumentoLetto[]> | null = null

/**
 * Catalogo dei tipi di documento (GET /documenti/tipi), letto una volta per sessione del browser: il
 * catalogo cambia solo con un rilascio. Una lettura non riuscita non resta in memoria, cosi' la
 * finestra successiva riprova.
 */
function useTipiDocumento(attivo: boolean) {
  const [tipi, setTipi] = useState<TipoDocumentoLetto[] | null>(null)
  const [errore, setErrore] = useState(false)
  useEffect(() => {
    if (!attivo) return
    let vivo = true
    tipiInCorso ??= api.get<TipoDocumentoLetto[]>('/documenti/tipi')
    tipiInCorso
      .then((t) => { if (vivo) setTipi(t) })
      .catch(() => { tipiInCorso = null; if (vivo) setErrore(true) })
    return () => { vivo = false }
  }, [attivo])
  return { tipi, errore }
}

/** Tipi di documento che provano il potere di firma (`giustifica_firmatario`). */
function useTipiGiustificativo(attivo: boolean) {
  const { tipi, errore } = useTipiDocumento(attivo)
  // Lo stesso elenco a ogni disegno: le finestre lo usano come dipendenza dei propri effetti.
  const filtrati = useMemo(() => tipi?.filter((v) => v.giustifica_firmatario) ?? null, [tipi])
  return { tipi: filtrati, errore }
}

/** Modulo in uso (GET /consensi/modulo): testi delle dichiarazioni, codice e revisione. */
export function useModuloConsensi(attivo: boolean) {
  const [modulo, setModulo] = useState<ModuloConsensi | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    if (!attivo) return
    let vivo = true
    api.get<ModuloConsensi>('/consensi/modulo')
      .then((m) => { if (vivo) setModulo(m) })
      .catch((e) => { if (vivo) setErrore(e instanceof ErroreApi ? e.message : 'Testi del modulo non disponibili.') })
    return () => { vivo = false }
  }, [attivo])
  return { modulo, errore }
}

// --- campi della sottoscrizione ----------------------------------------------------------------

/**
 * Presa visione e dichiarazioni del modulo, con luogo, data e firmatario. Si usa nella registrazione
 * dalla scheda e nella creazione della scheda: stessi campi, stesse verifiche (regole-consensi.ts).
 *
 * Quando firma un terzo compare il nome di chi firma e, sotto, `giustificativo`: il campo per caricare
 * il documento sul potere di firma. Nella creazione della scheda il documento non si puo' ancora
 * caricare, perche' la persona non esiste: al suo posto si spiega che si carica dalla scheda.
 */
export function CampiSottoscrizione({ modulo, stato: s, cambia, errori: e, oggi, giustificativo }: {
  modulo: ModuloConsensi
  stato: StatoSottoscrizione
  cambia: (parziale: Partial<StatoSottoscrizione>) => void
  errori: Errori
  oggi: string
  giustificativo?: ReactNode
}) {
  const scegli = (tipo: string, esito: '' | Esito) => cambia({ esiti: { ...s.esiti, [tipo]: esito } })
  const terzo = firmaDiTerzi(s.firmatario)
  return (
    <>
      <details className="persona-informativa">
        <summary>Informativa sintetica del modulo {modulo.codice} Rev {modulo.revisione}</summary>
        <p className="testo-libero">{modulo.informativa}</p>
      </details>
      <p className="persona-nota-sezione">«{modulo.dichiarazione}…»</p>

      {modulo.consensi.map((d) => (
        <GruppoScelta
          key={d.tipo}
          legenda={`${d.numero}. ${d.nome}${d.facoltativo ? ' (facoltativo)' : ''}`}
          descrizione={(
            <>
              <p>«Esprimo / Non esprimo {d.testo}»</p>
              {d.nota && <p className="piccolo tenue">{d.nota}</p>}
            </>
          )}
          opzioni={OPZIONI_ESITO}
          valore={s.esiti[d.tipo] ?? ''}
          cambia={(valore) => scegli(d.tipo, valore as Esito)}
          errore={e[`esiti.${d.tipo}`]}
          obbligatorio={!d.facoltativo}
          aiuto={d.facoltativo && s.esiti[d.tipo]
            ? <Bottone variante="discreto" dimensione="piccolo" onClick={() => scegli(d.tipo, '')}>Togli la scelta</Bottone>
            : undefined}
        />
      ))}
      {e.esiti && <p className="campo-errore" role="alert">{e.esiti}</p>}

      {/* Firma chi firma il modulo: con un terzo la presa visione e l'impegno li sottoscrive lui. */}
      <SpuntaObbligatoria
        etichetta={`${terzo ? maiuscola(nomeFirmatario(s.firmatario)) : 'La persona'} ha firmato la presa visione `
          + 'dell’informativa e l’impegno a comunicare le variazioni dei dati'}
        valore={s.presa_visione} cambia={(presa_visione) => cambia({ presa_visione })} errore={e.presa_visione} />

      <div className="griglia-modulo">
        <Campo etichetta="Luogo (Lì)" valore={s.luogo} cambia={(luogo) => cambia({ luogo })} obbligatorio
               maxLength={120} errore={e.luogo} aiuto="Come scritto sul modulo firmato." />
        <Campo tipo="date" etichetta="Data della firma" valore={s.data} cambia={(data) => cambia({ data })} obbligatorio
               min={modulo.data_revisione} max={oggi} errore={e.data} />
        <Scelta etichetta="Firmatario" valore={s.firmatario} cambia={(firmatario) => cambia({ firmatario })}
                opzioni={FIRMATARI} obbligatorio errore={e.firmatario} />
        {terzo && <CampoNomeFirmatario valore={s.firmatario_nome ?? ''} cambia={(firmatario_nome) => cambia({ firmatario_nome })}
                                       errore={e.firmatario_nome} />}
      </div>
      {terzo && (giustificativo ?? (
        <Avviso>
          {spiegazioneGiustificativo(s.firmatario)} Si carica dalla scheda, dopo il salvataggio: fino ad allora la firma
          risulta da completare, e la scheda indica entro quando.
        </Avviso>
      ))}
    </>
  )
}

const maiuscola = (testo: string) => testo.charAt(0).toUpperCase() + testo.slice(1)

/** Nome e cognome di chi firma al posto della persona: il documento sul potere di firma nomina una persona. */
function CampoNomeFirmatario({ valore, cambia, errore }: { valore: string; cambia: (v: string) => void; errore?: string }) {
  return (
    <Campo etichetta="Nome e cognome di chi firma" valore={valore} cambia={cambia} obbligatorio maxLength={120}
           autoComplete="off" errore={errore} aiuto="Come risulta dal documento che dà il potere di firma." />
  )
}

/**
 * Scelta di un file da caricare tra i documenti della scheda: si carica cifrato, come ogni documento.
 * Il comando apre il selettore; il nome del file scelto resta accanto, con il comando per toglierlo.
 */
function SceltaFile({ etichetta, comando, nota, file, cambia, errore, bloccata, obbligatoria = false }: {
  etichetta: string; comando: string; nota: ReactNode
  file: File | null; cambia: (file: File | null) => void; errore?: string | null; bloccata: boolean; obbligatoria?: boolean
}) {
  const ingresso = useRef<HTMLInputElement>(null)
  const id = useId()
  return (
    <div className={`campo persona-scansione${errore ? ' campo-errato' : ''}`}>
      <span className={`persona-derivato-etichetta${obbligatoria ? ' obbligatorio' : ''}`} id={`${id}-etichetta`}>
        {etichetta}
      </span>
      <input ref={ingresso} type="file" hidden accept="application/pdf,image/*"
             onChange={(ev) => { const scelto = ev.target.files?.[0]; ev.target.value = ''; if (scelto) cambia(scelto) }} />
      <div className="riga" aria-labelledby={`${id}-etichetta`} role="group">
        <Bottone variante="secondario" icona={<IconaCarica />} disabled={bloccata} onClick={() => ingresso.current?.click()}
                 aria-describedby={`${id}-nota`}>
          {file ? 'Scegli un altro file' : comando}
        </Bottone>
        {file && <span className="persona-scansione-nome">{file.name}</span>}
        {file && !bloccata && (
          <Bottone variante="discreto" dimensione="piccolo" onClick={() => cambia(null)}>Togli il file</Bottone>
        )}
      </div>
      {errore
        ? <p className="campo-errore" id={`${id}-nota`} role="alert">{errore}</p>
        : <p className="campo-aiuto" id={`${id}-nota`}>{nota}</p>}
    </div>
  )
}

const NOTA_FILE = 'PDF o immagine fino a 50 MB; si salva subito e si scarica dopo il controllo antivirus.'

/** Scelta del file della scansione del modulo firmato, o della revoca firmata. */
function SceltaScansione({ file, cambia, errore, bloccata, obbligatoria = false, revoca = false }: {
  file: File | null; cambia: (file: File | null) => void; errore?: string | null; bloccata: boolean; obbligatoria?: boolean
  revoca?: boolean
}) {
  return (
    <SceltaFile etichetta={revoca ? 'Scansione della revoca firmata' : 'Scansione del modulo firmato'}
                comando="Scegli la scansione" file={file} cambia={cambia}
                errore={errore} bloccata={bloccata} obbligatoria={obbligatoria}
                nota={`${obbligatoria ? '' : 'Facoltativa: si carica anche dopo, con «Carica il modulo firmato». '}${NOTA_FILE}`} />
  )
}

/** Documento sul potere di firma scelto nella registrazione: il file e, se il catalogo ne offre piu' d'uno, il tipo. */
type GiustificativoScelto = { file: File | null; tipo: string }

const giustificativoVuoto = (): GiustificativoScelto => ({ file: null, tipo: '' })

/** Tipo con cui si carica il documento: quello scelto, o l'unico che il catalogo offre per il firmatario. */
const tipoDelGiustificativo = (scelto: GiustificativoScelto, ammessi: TipoGiustificativo[]) =>
  ammessi.length === 1 ? ammessi[0].codice : ammessi.some((t) => t.codice === scelto.tipo) ? scelto.tipo : ''

/**
 * Documento sul potere di firma nella registrazione dei consensi o della revoca. E' facoltativo,
 * perche' spesso arriva dopo: senza, la firma resta da completare e si completa dall'avviso della
 * sezione. Si carica con il tipo che prova il potere di quel firmatario.
 */
function SceltaGiustificativo({ firmatario, tipi, stato, cambia, errori, bloccata }: {
  firmatario: string; tipi: TipoGiustificativo[] | null; stato: GiustificativoScelto
  cambia: (parziale: Partial<GiustificativoScelto>) => void; errori: Errori; bloccata: boolean
}) {
  const ammessi = tipiGiustificativo(tipi ?? [], firmatario)
  return (
    <>
      <SceltaFile etichetta="Documento sul potere di firma" comando="Scegli il documento" file={stato.file}
                  cambia={(file) => cambia({ file })} errore={errori.giustificativo} bloccata={bloccata}
                  nota={(
                    <>
                      {spiegazioneGiustificativo(firmatario)} Se adesso non è disponibile, registra lo stesso: la firma
                      resta da completare e il documento si aggiunge dopo, entro il termine che la scheda indica.
                      {' '}{NOTA_FILE}
                    </>
                  )} />
      {stato.file && ammessi.length > 1 && (
        <Scelta etichetta="Che documento è" valore={stato.tipo} cambia={(tipo) => cambia({ tipo })} vuota="Scegli…"
                opzioni={ammessi.map((t) => ({ valore: t.codice, etichetta: t.nome }))} obbligatorio
                errore={errori.tipo_giustificativo} />
      )}
    </>
  )
}

/**
 * Verifica del documento sul potere di firma prima dell'invio: con un file scelto serve un tipo, e
 * se il catalogo non si e' potuto leggere il tipo non si conosce.
 */
function erroriGiustificativo(firmatario: string, scelto: GiustificativoScelto, tipi: TipoGiustificativo[] | null): Errori {
  if (!firmaDiTerzi(firmatario) || !scelto.file) return {}
  if (!tipi) {
    return { giustificativo: 'I tipi di documento non sono disponibili in questo momento: togli il file e registra senza, '
      + 'il documento si aggiunge dopo.' }
  }
  const ammessi = tipiGiustificativo(tipi, firmatario)
  if (!ammessi.length) return { giustificativo: 'Per questa firma il catalogo non prevede un tipo di documento.' }
  return tipoDelGiustificativo(scelto, ammessi) ? {} : { tipo_giustificativo: 'Indicare che documento è.' }
}

// --- indicatore e avviso -----------------------------------------------------------------------

/** Consensi 1 e 2: espressi, negati, revocati o non raccolti, e l'effetto sulla registrazione degli interventi. */
function IndicatoreConsensi({ obbligatori: o }: { obbligatori: ConsensiLetti['obbligatori'] }) {
  const c = o.conteggi
  return (
    <div className="persona-consensi-indicatore">
      <p className="persona-sottotitolo">Consensi necessari per registrare interventi</p>
      <ul className="persona-etichette">
        {o.voci.map((v) => (
          <li key={v.tipo}>
            <Etichetta tono={TONO_ESITO[v.esito]}>{v.numero}. {ETICHETTE_ESITO[v.esito]}</Etichetta>
          </li>
        ))}
      </ul>
      <p className="piccolo tenue">
        {c.espressi} espressi · {c.negati} negati · {c.revocati} revocati · {c.mancanti} non raccolti
      </p>
      {o.registrazione_interventi === 'bloccata' && (
        <p className="piccolo">
          Su questa scheda gli interventi si registrano solo dopo aver registrato i consensi 1 e 2 espressi.
        </p>
      )}
    </div>
  )
}

/**
 * Avviso persistente sulle schede migrate senza i consensi 1 e 2 espressi (D-08): non si chiude,
 * perche' resta vero finche' i consensi non si raccolgono. La registrazione degli interventi non si
 * blocca.
 */
export function AvvisoSchedaMigrata({ consensi }: { consensi: ConsensiLetti | undefined }) {
  if (!consensi || consensi.obbligatori.registrazione_interventi !== 'avviso') return null
  return (
    <Avviso tipo="attenzione">
      <strong>Scheda dell’archivio precedente senza i consensi della Scheda utente.</strong>{' '}
      {consensi.obbligatori.voci.map((v) => `${v.numero}. ${v.nome}: ${ETICHETTE_ESITO[v.esito]}`).join('; ')}.
      {' '}Gli interventi si registrano con un avviso; i consensi vanno raccolti sul modulo firmato al primo contatto.
    </Avviso>
  )
}

/**
 * Firme di terzi a cui manca il documento sul potere di firma (454), con il termine per completarle
 * (456) e il comando che apre il percorso guidato. I consensi sono registrati e valgono per la
 * registrazione degli interventi: il documento serve a dimostrare che chi ha firmato al posto della
 * persona ne aveva il potere.
 */
function AvvisoFirmeDaCompletare({ firme, completare, apri }: {
  firme: FirmaDaCompletare[]; completare: boolean; apri: (firma: FirmaDaCompletare) => void
}) {
  if (!firme.length) return null
  return (
    <Avviso tipo="attenzione">
      <strong>{firme.length === 1 ? 'Una firma da completare.' : `${firme.length} firme da completare.`}</strong>{' '}
      Chi firma al posto della persona deve risultare autorizzato a farlo, e {firme.length === 1 ? 'qui' : 'in questi casi'}
      {' '}manca il documento che lo prova. I consensi sono registrati e gli interventi non si bloccano; il documento va
      aggiunto entro il termine, e quando il termine scade ne viene avvisata anche la Direzione.
      <ul className="elenco-avviso">
        {firme.map((f) => (
          <li key={f.consenso_id}>
            <span className="riga">
              <span>
                {descriviFirma(f)}
                {f.giustificativo_da_sostituire
                  ? ': il documento collegato è stato cancellato o non ha superato il controllo antivirus' : ''}
                {f.vigente ? '' : ' (non più in vigore)'}.
                {' '}<Etichetta tono={f.termine_scaduto ? 'critico' : 'attenzione'}>{termineFirma(f)}</Etichetta>
              </span>
              {completare && (
                <Bottone variante="secondario" dimensione="piccolo" onClick={() => apri(f)}>Completa</Bottone>
              )}
            </span>
          </li>
        ))}
      </ul>
      {!completare && (
        <p>La firma la completa chi registra i consensi e carica documenti su questa scheda.</p>
      )}
    </Avviso>
  )
}

/** Chi ha firmato: il firmatario, il nome se firma un terzo, e l'etichetta se la firma e' da completare. */
function Firmatario({ consenso: c }: { consenso: ConsensoLetto | null }) {
  if (!c?.firmatario) return <span className="tenue">—</span>
  return (
    <>
      {nomeFirmatario(c.firmatario)}
      {c.firmatario_nome && <span className="piccolo tenue"> · {c.firmatario_nome}</span>}
      {c.da_completare && <> <Etichetta tono="attenzione">da completare</Etichetta></>}
    </>
  )
}

/**
 * Modulo firmato delle dichiarazioni in vigore (474): un comando solo, «Carica il modulo firmato»,
 * per tutte quelle che non lo hanno. Il modulo e' facoltativo (D-09): senza, i consensi valgono come
 * registrati, e la riga lo dice senza allarmi.
 */
function ModuloFirmatoSezione({ modulo, caricare, apri, scarica }: {
  modulo: ModuloFirmato | undefined; caricare: boolean; apri: () => void; scarica: ((documento: string) => void) | null
}) {
  const stato = statoModuloFirmato(modulo)
  if (!modulo || !stato) return null
  return (
    <div className="persona-modulo-firmato">
      <p className="persona-sottotitolo">Modulo firmato</p>
      {stato === 'collegato'
        ? (
          <div className="riga">
            <span>Collegato a tutte le dichiarazioni in vigore.</span>
            {modulo.collegati.map((m) => (m.antivirus === 'in_attesa'
              ? <Etichetta key={m.id} tono="informativo">{ETICHETTA_IN_VERIFICA}</Etichetta>
              : scarica && (
                <Bottone key={m.id} variante="secondario" dimensione="piccolo" icona={<IconaScarica />}
                         onClick={() => scarica(m.id)}>
                  {modulo.collegati.length > 1 ? `Scarica (${m.dichiarazioni.map((d) => d.numero).join(', ')})` : 'Scarica'}
                </Bottone>
              )))}
          </div>
          )
        : (
          <div className="riga">
            <span>
              Non ancora caricato per {descriviDaCollegare(modulo)}. È facoltativo: i consensi valgono come registrati.
            </span>
            {caricare && (
              <Bottone variante="secondario" dimensione="piccolo" icona={<IconaCarica />} onClick={apri}>
                Carica il modulo firmato
              </Bottone>
            )}
          </div>
          )}
    </div>
  )
}

// --- sezione della scheda ----------------------------------------------------------------------

type Aperta =
  | { tipo: 'sottoscrizione' }
  | { tipo: 'revoca'; voce: VoceVigente }
  | { tipo: 'scansione'; sottoscrizione: string }
  | { tipo: 'modulo' }
  | { tipo: 'completamento'; firma: FirmaDaCompletare }

export function ConsensiScheda({ scheda, ricarica }: { scheda: SchedaLetta; ricarica: Ricarica }) {
  const { puo } = useSessione()
  const consensi = scheda.consensi
  const registrare = !!scheda.puo.registrareConsensi
  // Il perimetro della scheda lo verifica l'API; qui basta non offrire il caricamento a chi non carica.
  const caricare = puo('documento.carica') && puo('documento.leggi')
  const { modulo, errore: erroreModulo } = useModuloConsensi(registrare)
  const [aperta, setAperta] = useState<Aperta | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  const conclusa = async (messaggio: string) => {
    setAperta(null); setErrore(null); setEsito(messaggio)
    await ricarica()
  }

  async function scarica(documento: string) {
    setErrore(null)
    try {
      const { url } = await api.get<{ url: string }>(`/persone/${scheda.id}/documenti/${documento}/scarica`)
      window.open(url, '_blank', 'noopener')
    } catch (e) { setErrore(messaggioDi(e)) }
  }

  /**
   * Il modulo firmato della riga. Niente comando per riga: il modulo e' un foglio solo e si carica una
   * volta, con «Carica il modulo firmato» sopra la tabella (474). Fa eccezione la revoca, che e' un atto
   * a parte con il suo foglio e si collega dalla sua riga.
   */
  const scansione = (c: ConsensoLetto | null): ReactNode => {
    if (!c) return <span className="tenue">—</span>
    if (c.documento?.valido) {
      if (c.documento.antivirus === 'in_attesa') return <Etichetta tono="informativo">{ETICHETTA_IN_VERIFICA}</Etichetta>
      return puo('documento.leggi')
        ? (
          <Bottone variante="secondario" dimensione="piccolo" icona={<IconaScarica />}
                   onClick={() => void scarica(c.documento!.id)}>Scarica</Bottone>
        )
        : 'collegato'
    }
    if (c.stato === 'revocato' && !c.documento && registrare && caricare && c.sottoscrizione_id) {
      const sottoscrizione = c.sottoscrizione_id
      return (
        <Bottone variante="secondario" dimensione="piccolo" icona={<IconaCarica />}
                 onClick={() => setAperta({ tipo: 'scansione', sottoscrizione })}>Collega la revoca firmata</Bottone>
      )
    }
    if (c.documento) return <span className="tenue">{c.documento.cancellato ? 'cancellato' : 'non valido'}</span>
    return <span className="tenue">—</span>
  }

  return (
    <Riquadro
      titolo="Consensi"
      azioni={registrare && modulo
        ? <Bottone variante="primario" dimensione="piccolo" onClick={() => setAperta({ tipo: 'sottoscrizione' })}>Registra i consensi</Bottone>
        : undefined}
    >
      {!consensi
        ? <p className="tenue piccolo">I consensi non rientrano negli incarichi in corso su questa scheda.</p>
        : (
          <>
            {/* 481: chi non puo' firmare l'informativa non ha consensi da raccogliere. */}
            {scheda.dati_anonimizzati
              ? (
                <Avviso tipo="informazione">
                  Scheda con dati anonimizzati: la persona non firma l’informativa, i consensi non si raccolgono e gli
                  interventi si registrano senza. Togliendo «Dati anonimizzati» dai dati anagrafici, i nuovi interventi
                  chiedono i consensi 1 e 2.
                </Avviso>
              )
              : <IndicatoreConsensi obbligatori={consensi.obbligatori} />}
            <ModuloFirmatoSezione modulo={consensi.modulo_firmato} caricare={registrare && caricare}
                                  apri={() => setAperta({ tipo: 'modulo' })}
                                  scarica={puo('documento.leggi') ? (documento) => void scarica(documento) : null} />
            {esito && <p className="piccolo" role="status">{esito}</p>}
            {erroreModulo && <Avviso tipo="errore">{erroreModulo}</Avviso>}
            {errore && <Avviso tipo="errore">{errore}</Avviso>}
            <AvvisoFirmeDaCompletare firme={consensi.da_completare ?? []} completare={registrare && caricare}
                                     apri={(firma) => setAperta({ tipo: 'completamento', firma })} />

            <Tabella intestazioni={['Dichiarazione', 'Esito', 'Data', 'Luogo', 'Firmatario', 'Modulo firmato',
              <span key="comandi" className="solo-lettore">Comandi</span>]}>
              {consensi.vigenti.map((v) => {
                const c = v.consenso
                return (
                  <tr key={v.tipo}>
                    <td data-etichetta="Dichiarazione">{v.numero}. {v.nome}</td>
                    <td data-etichetta="Esito"><Etichetta tono={TONO_ESITO[v.esito]}>{ETICHETTE_ESITO[v.esito]}</Etichetta></td>
                    <td data-etichetta="Data">
                      {c ? giornoItaliano(v.esito === 'revocato' ? c.revocato_il ?? c.data_consenso : c.data_consenso) : '—'}
                    </td>
                    <td data-etichetta="Luogo">{c?.luogo || '—'}</td>
                    <td data-etichetta="Firmatario"><Firmatario consenso={c} /></td>
                    <td data-etichetta="Modulo firmato">{scansione(c)}</td>
                    <td data-etichetta="Comandi">
                      {registrare && v.esito === 'espresso' && (
                        <Bottone variante="secondario" dimensione="piccolo"
                                 onClick={() => setAperta({ tipo: 'revoca', voce: v })}>Registra la revoca</Bottone>
                      )}
                    </td>
                  </tr>
                )
              })}
            </Tabella>

            {consensi.storico.length > 0 && (
              <details className="persona-storico-consensi">
                <summary>Registro completo ({consensi.storico.length} {consensi.storico.length === 1 ? 'riga' : 'righe'})</summary>
                <Tabella intestazioni={['Registrata il', 'Dichiarazione', 'Esito', 'Data', 'Firmatario', 'Modulo', 'Registrata da']}>
                  {consensi.storico.map((r) => (
                    <tr key={r.id}>
                      <td data-etichetta="Registrata il">{new Date(r.registrato_il).toLocaleString('it-IT')}</td>
                      <td data-etichetta="Dichiarazione">{nomeTipoConsenso(r)}</td>
                      <td data-etichetta="Esito">
                        {nomeStatoConsenso(r.stato)}
                        {r.note && <span className="piccolo tenue"> · {r.note}</span>}
                      </td>
                      <td data-etichetta="Data">{giornoItaliano(r.stato === 'revocato' ? r.revocato_il ?? r.data_consenso : r.data_consenso) || '—'}</td>
                      <td data-etichetta="Firmatario"><Firmatario consenso={r} /></td>
                      <td data-etichetta="Modulo">{r.versione_informativa ?? '—'}</td>
                      <td data-etichetta="Registrata da">
                        {r.registrato_da ? `${r.registrato_da.nome} ${r.registrato_da.cognome}`.trim() : 'migrazione'}
                      </td>
                    </tr>
                  ))}
                </Tabella>
              </details>
            )}
          </>
        )}

      {aperta?.tipo === 'sottoscrizione' && modulo && (
        <ModaleSottoscrizione personaId={scheda.id} modulo={modulo} caricare={caricare}
                              chiudi={() => setAperta(null)} conclusa={conclusa} />
      )}
      {aperta?.tipo === 'revoca' && (
        <ModaleRevoca personaId={scheda.id} voce={aperta.voce} caricare={caricare}
                      chiudi={() => setAperta(null)} conclusa={conclusa} />
      )}
      {aperta?.tipo === 'scansione' && (
        <ModaleScansione personaId={scheda.id} sottoscrizione={aperta.sottoscrizione}
                         chiudi={() => setAperta(null)} conclusa={conclusa} />
      )}
      {aperta?.tipo === 'modulo' && consensi && (
        <ModaleModuloFirmato personaId={scheda.id} modulo={consensi.modulo_firmato}
                             chiudi={() => setAperta(null)} conclusa={conclusa} />
      )}
      {aperta?.tipo === 'completamento' && (
        <PercorsoCompletamento personaId={scheda.id} firma={aperta.firma}
                               chiudi={() => setAperta(null)} conclusa={conclusa} />
      )}
    </Riquadro>
  )
}

// --- finestre ---------------------------------------------------------------------------------

/** Stato comune delle finestre: errori per campo, messaggio in testa, invio in corso, fuoco sul primo errore. */
function useInvioConsensi() {
  const radice = useRef<HTMLFormElement>(null)
  const [errori, setErrori] = useState<Errori>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [tentativo, setTentativo] = useState(0)
  useFuocoSulPrimoErrore(radice, tentativo)
  const segnala = (nuovi: Errori) => { setErrori(nuovi); setErrore('Correggere i campi segnalati.'); setTentativo((t) => t + 1) }
  /** Errore dell'API: sul campo se lo riguarda, altrimenti in testa. */
  const fallito = (e: unknown) => {
    const campi = e instanceof ErroreApi ? erroriDaApi(e) : {}
    if (nessunErrore(campi)) { setErrori({}); setErrore(messaggioDi(e)) } else segnala(campi)
  }
  const azzera = () => { setErrori({}); setErrore(null) }
  const inizia = () => { azzera(); setInCorso(true) }
  return { radice, errori, errore, inCorso, setInCorso, segnala, fallito, inizia, azzera }
}

/**
 * Carica il documento sul potere di firma scelto nella registrazione, una volta: se poi la
 * registrazione non riesce, un nuovo invio riusa lo stesso documento invece di caricarlo di nuovo.
 */
function useCaricamentoGiustificativo(personaId: string) {
  const [caricato, setCaricato] = useState<{ file: File; tipo: string; id: string } | null>(null)
  return async (scelto: GiustificativoScelto, tipo: string): Promise<string | null> => {
    if (!scelto.file) return null
    if (caricato?.file === scelto.file && caricato.tipo === tipo) return caricato.id
    const id = await caricaDocumento(personaId, { nome: scelto.file.name, tipo, dati: scelto.file })
    setCaricato({ file: scelto.file, tipo, id })
    return id
  }
}

/** Risposta delle registrazioni: se la firma resta da completare, entro quando (456). */
type EsitoFirma = { da_completare: boolean; completare_entro: string | null }

/** Esito della registrazione in una frase: se firma un terzo senza documento, lo dice con il termine. */
const esitoRegistrazione = (base: string, firmatario: string, esito: EsitoFirma) =>
  esito.da_completare
    ? `${base} La firma resta da completare${esito.completare_entro ? ` entro il ${giornoItaliano(esito.completare_entro)}` : ''}: `
      + `manca il documento sul potere di firma ${firmaDi(firmatario)}.`
    : base

/** Anche dal riquadro «Moduli dei progetti» (495): il modulo CIDIS firmato insieme a quello del progetto. */
export function ModaleSottoscrizione({ personaId, modulo, caricare, chiudi, conclusa }: {
  personaId: string; modulo: ModuloConsensi; caricare: boolean; chiudi: () => void; conclusa: (messaggio: string) => Promise<void>
}) {
  const oggi = oggiLocale()
  const [stato, setStato] = useState(() => sottoscrizioneNuova(oggi))
  const [file, setFile] = useState<File | null>(null)
  // Documento gia' caricato: se la registrazione non riesce, un nuovo invio non lo carica una seconda volta.
  const [documento, setDocumento] = useState<string | null>(null)
  const [giustificativo, setGiustificativo] = useState(giustificativoVuoto)
  const { tipi } = useTipiGiustificativo(caricare)
  const caricaGiustificativo = useCaricamentoGiustificativo(personaId)
  const invio = useInvioConsensi()
  const terzo = firmaDiTerzi(stato.firmatario)

  async function invia() {
    const verificati = {
      ...verificaSottoscrizione(stato, modulo, oggi),
      ...(caricare ? erroriGiustificativo(stato.firmatario, giustificativo, tipi) : {}),
    }
    if (!nessunErrore(verificati)) { invio.segnala(verificati); return }
    invio.inizia()
    try {
      let collegato = documento
      if (file && !collegato) {
        collegato = await caricaDocumento(personaId, { nome: file.name, tipo: TIPO_DOCUMENTO_FIRMATO, dati: file })
        setDocumento(collegato)
      }
      // Il documento sul potere di firma si carica solo se firma un terzo: scelto e poi lasciato con
      // l'interessato, resta fuori.
      const potere = terzo && caricare
        ? await caricaGiustificativo(giustificativo, tipoDelGiustificativo(giustificativo, tipiGiustificativo(tipi ?? [], stato.firmatario)))
        : null
      const esito = await api.post<EsitoFirma>(`/persone/${personaId}/consensi`,
        corpoSottoscrizione(stato, modulo, collegato, potere))
      await conclusa(esitoRegistrazione('Consensi registrati.', stato.firmatario, esito))
    } catch (e) {
      invio.fallito(e)
    } finally { invio.setInCorso(false) }
  }

  return (
    <Modale titolo="Registrazione dei consensi" chiudi={chiudi} larga
            azioni={(
              <>
                <Bottone variante="secondario" onClick={chiudi} disabled={invio.inCorso}>Annulla</Bottone>
                <Bottone variante="primario" inCorso={invio.inCorso} onClick={() => void invia()}>Registra</Bottone>
              </>
            )}>
      <form ref={invio.radice} noValidate onSubmit={(ev) => { ev.preventDefault(); void invia() }}>
        <p className="persona-nota-sezione">
          Si trascrivono le scelte del modulo {modulo.codice} Rev {modulo.revisione} firmato dalla persona, o da chi firma
          per lei. I campi contrassegnati con * sono obbligatori; la dichiarazione facoltativa lasciata senza scelta resta
          non raccolta.
        </p>
        {invio.errore && <Avviso tipo="errore">{invio.errore}</Avviso>}
        <CampiSottoscrizione modulo={modulo} stato={stato} cambia={(p) => setStato((s) => ({ ...s, ...p }))}
                             errori={invio.errori} oggi={oggi}
                             giustificativo={caricare
                               ? (
                                 <SceltaGiustificativo firmatario={stato.firmatario} tipi={tipi} stato={giustificativo}
                                                       cambia={(p) => setGiustificativo((g) => ({ ...g, ...p }))}
                                                       errori={invio.errori} bloccata={invio.inCorso} />
                                 )
                               : (
                                 <Avviso>
                                   {spiegazioneGiustificativo(stato.firmatario)} Con gli incarichi in corso non puoi caricare
                                   documenti su questa scheda: i consensi si registrano e la firma resta da completare.
                                 </Avviso>
                                 )} />
        {caricare && (
          <SceltaScansione file={file} cambia={setFile} errore={invio.errori.documento} bloccata={!!documento} />
        )}
      </form>
    </Modale>
  )
}

function ModaleRevoca({ personaId, voce, caricare, chiudi, conclusa }: {
  personaId: string; voce: VoceVigente; caricare: boolean; chiudi: () => void; conclusa: (messaggio: string) => Promise<void>
}) {
  const oggi = oggiLocale()
  const [stato, setStato] = useState<StatoRevoca>(() => revocaNuova(oggi))
  const [giustificativo, setGiustificativo] = useState(giustificativoVuoto)
  const { tipi } = useTipiGiustificativo(caricare)
  const caricaGiustificativo = useCaricamentoGiustificativo(personaId)
  const invio = useInvioConsensi()
  const dataConsenso = voce.consenso?.data_consenso ?? null
  const cambia = (p: Partial<StatoRevoca>) => setStato((s) => ({ ...s, ...p }))
  const terzo = firmaDiTerzi(stato.firmatario)

  async function invia() {
    const verificati = {
      ...verificaRevoca(stato, oggi, dataConsenso),
      ...(caricare ? erroriGiustificativo(stato.firmatario, giustificativo, tipi) : {}),
    }
    if (!nessunErrore(verificati)) { invio.segnala(verificati); return }
    invio.inizia()
    try {
      const potere = terzo && caricare
        ? await caricaGiustificativo(giustificativo, tipoDelGiustificativo(giustificativo, tipiGiustificativo(tipi ?? [], stato.firmatario)))
        : null
      const esito = await api.post<EsitoFirma>(`/persone/${personaId}/consensi/revoche`,
        corpoRevoca(stato, voce.tipo, null, potere))
      await conclusa(esitoRegistrazione(`Revoca del consenso ${voce.numero} registrata.`, stato.firmatario, esito))
    } catch (e) {
      invio.fallito(e)
    } finally { invio.setInCorso(false) }
  }

  return (
    <Modale titolo={`Revoca del consenso ${voce.numero}`} chiudi={chiudi}
            azioni={(
              <>
                <Bottone variante="secondario" onClick={chiudi} disabled={invio.inCorso}>Annulla</Bottone>
                <Bottone variante="critico" inCorso={invio.inCorso} onClick={() => void invia()}>Registra la revoca</Bottone>
              </>
            )}>
      <form ref={invio.radice} noValidate onSubmit={(ev) => { ev.preventDefault(); void invia() }}>
        <Avviso tipo="attenzione">
          {voce.nome}: la revoca si aggiunge al registro e il consenso espresso il {giornoItaliano(dataConsenso)} resta
          nello storico.{voce.necessario_interventi
            ? ' Sulle schede nuove, dopo la revoca, gli interventi non si registrano.' : ''}
        </Avviso>
        {invio.errore && <Avviso tipo="errore">{invio.errore}</Avviso>}
        <div className="griglia-modulo">
          <Campo tipo="date" etichetta="Data della revoca" valore={stato.data} cambia={(data) => cambia({ data })}
                 obbligatorio min={dataConsenso ?? undefined} max={oggi} errore={invio.errori.data} />
          <Scelta etichetta="Firmatario" valore={stato.firmatario} cambia={(firmatario) => cambia({ firmatario })}
                  opzioni={FIRMATARI} obbligatorio errore={invio.errori.firmatario} />
          {terzo && <CampoNomeFirmatario valore={stato.firmatario_nome ?? ''}
                                         cambia={(firmatario_nome) => cambia({ firmatario_nome })}
                                         errore={invio.errori.firmatario_nome} />}
          <Campo etichetta="Luogo" valore={stato.luogo} cambia={(luogo) => cambia({ luogo })} maxLength={120}
                 errore={invio.errori.luogo} aiuto="Facoltativo." />
        </div>
        {terzo && (caricare
          ? (
            <SceltaGiustificativo firmatario={stato.firmatario} tipi={tipi} stato={giustificativo}
                                  cambia={(p) => setGiustificativo((g) => ({ ...g, ...p }))}
                                  errori={invio.errori} bloccata={invio.inCorso} />
            )
          : (
            <Avviso>
              {spiegazioneGiustificativo(stato.firmatario)} Con gli incarichi in corso non puoi caricare documenti su
              questa scheda: la revoca si registra e la firma resta da completare.
            </Avviso>
            ))}
      </form>
    </Modale>
  )
}

function ModaleScansione({ personaId, sottoscrizione, chiudi, conclusa }: {
  personaId: string; sottoscrizione: string; chiudi: () => void; conclusa: (messaggio: string) => Promise<void>
}) {
  const [file, setFile] = useState<File | null>(null)
  const [documento, setDocumento] = useState<string | null>(null)
  const invio = useInvioConsensi()

  async function invia() {
    if (!file && !documento) { invio.segnala({ documento: 'Scegliere il file della scansione.' }); return }
    invio.inizia()
    try {
      let collegato = documento
      if (!collegato && file) {
        collegato = await caricaDocumento(personaId, { nome: file.name, tipo: TIPO_DOCUMENTO_FIRMATO, dati: file })
        setDocumento(collegato)
      }
      await api.put(`/persone/${personaId}/consensi/sottoscrizioni/${sottoscrizione}/documento`, { documento_id: collegato })
      await conclusa(`Revoca firmata collegata. ${attesaAntivirus(new Date()).verifica}`)
    } catch (e) {
      invio.fallito(e)
    } finally { invio.setInCorso(false) }
  }

  return (
    <Modale titolo="Scansione della revoca firmata" chiudi={chiudi}
            azioni={(
              <>
                <Bottone variante="secondario" onClick={chiudi} disabled={invio.inCorso}>Annulla</Bottone>
                <Bottone variante="primario" inCorso={invio.inCorso} onClick={() => void invia()}>Collega</Bottone>
              </>
            )}>
      <form ref={invio.radice} noValidate onSubmit={(ev) => { ev.preventDefault(); void invia() }}>
        <p className="persona-nota-sezione">
          La revoca è un atto a parte, con il suo foglio: la scansione si carica tra i documenti della scheda e si
          collega a questa revoca, una volta sola.
        </p>
        {invio.errore && <Avviso tipo="errore">{invio.errore}</Avviso>}
        <SceltaScansione file={file} cambia={setFile} errore={invio.errori.documento} bloccata={!!documento} obbligatoria
                         revoca />
      </form>
    </Modale>
  )
}

/**
 * «Carica il modulo firmato» (474): un file solo, caricato adesso oppure gia' tra i documenti della
 * scheda di un tipo che vale come modulo (per esempio «informativa o consenso firmato» caricato dalla
 * linguetta Documenti), collegato a tutte le dichiarazioni in vigore che non hanno il modulo
 * (PUT /persone/:id/consensi/modulo-firmato). Il file caricato e non collegato non si ricarica. Si
 * apre anche dal riquadro «Moduli dei progetti» (495).
 */
export function ModaleModuloFirmato({ personaId, modulo, chiudi, conclusa }: {
  personaId: string; modulo: ModuloFirmato; chiudi: () => void; conclusa: (messaggio: string) => Promise<void>
}) {
  const { tipi, errore: erroreTipi } = useTipiDocumento(true)
  const ammessi = useMemo(() => tipiModulo(tipi ?? []), [tipi])
  const [esistenti, setEsistenti] = useState<DocumentoScheda[] | null>(null)
  const [stato, setStato] = useState<StatoModulo | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [caricato, setCaricato] = useState<{ file: File; id: string } | null>(null)
  const invio = useInvioConsensi()

  // Documenti della scheda che valgono come modulo: non cancellati, non infetti, di un tipo ammesso.
  useEffect(() => {
    if (!tipi) return
    let vivo = true
    Promise.all(ammessi.map((t) => api.get<DocumentoScheda[]>(
      `/persone/${personaId}/documenti?tipo=${encodeURIComponent(t.codice)}&limite=50`)))
      .then((liste) => liste.flat().filter((d) => d.antivirus !== 'infetto' && d.origine !== 'quarantena'))
      .catch(() => [] as DocumentoScheda[])
      .then((trovati) => {
        if (!vivo) return
        setEsistenti(trovati)
        setStato((s) => s ?? moduloNuovo(trovati.length > 0))
      })
    return () => { vivo = false }
  }, [tipi, ammessi, personaId])

  const nomeTipo = (codice: string) => tipi?.find((t) => t.codice === codice)?.nome ?? 'documento'
  const cambia = (p: Partial<StatoModulo>) => setStato((s) => (s ? { ...s, ...p } : s))

  async function invia() {
    if (!stato) return
    const errori = verificaModulo(stato, { file: !!file })
    if (!nessunErrore(errori)) { invio.segnala(errori); return }
    invio.inizia()
    try {
      let documento = stato.origine === 'esistente' ? stato.documento_esistente : null
      if (stato.origine === 'nuovo' && file) {
        if (caricato?.file === file) documento = caricato.id
        else {
          documento = await caricaDocumento(personaId, { nome: file.name, tipo: TIPO_DOCUMENTO_FIRMATO, dati: file })
          setCaricato({ file, id: documento })
        }
      }
      if (!documento) { invio.segnala({ documento: 'Scegliere il file del modulo firmato.' }); return }
      await api.put(`/persone/${personaId}/consensi/modulo-firmato`, corpoModulo(documento))
      const scelto = esistenti?.find((d) => d.id === documento)
      const verifica = stato.origine === 'nuovo' ? ` ${attesaAntivirus(new Date()).verifica}`
        : scelto?.antivirus === 'in_attesa' ? ` ${attesaAntivirus(scelto.caricato_il).verifica}` : ''
      await conclusa(`Modulo firmato collegato: vale per ${descriviDaCollegare(modulo)}.${verifica}`)
    } catch (e) {
      // Collegato da un altro operatore nel frattempo: non c'e' niente da correggere.
      if (e instanceof ErroreApi && e.codice === 'MODULO_GIA_COLLEGATO') {
        await conclusa('Il modulo firmato era già stato collegato da un altro operatore.')
        return
      }
      invio.fallito(e)
    } finally { invio.setInCorso(false) }
  }

  const pronto = !!stato && !!esistenti
  return (
    <Modale titolo="Modulo firmato dei consensi" chiudi={chiudi}
            azioni={(
              <>
                <Bottone variante="secondario" onClick={chiudi} disabled={invio.inCorso}>Annulla</Bottone>
                {pronto && (
                  <Bottone variante="primario" inCorso={invio.inCorso} onClick={() => void invia()}>
                    {stato.origine === 'esistente' ? 'Collega' : 'Carica e collega'}
                  </Bottone>
                )}
              </>
            )}>
      <form ref={invio.radice} noValidate onSubmit={(ev) => { ev.preventDefault(); void invia() }}>
        <p className="persona-nota-sezione">
          Il modulo è un foglio solo con tutte le dichiarazioni: si carica una volta e vale per{' '}
          {descriviDaCollegare(modulo)}. Gli esiti restano quelli registrati.
        </p>
        {invio.errore && <Avviso tipo="errore">{invio.errore}</Avviso>}
        {erroreTipi
          ? <Avviso tipo="errore">I tipi di documento non sono disponibili in questo momento. Riprova tra poco.</Avviso>
          : !pronto
            ? <Attesa testo="Preparazione…" />
            : (
              <>
                {esistenti.length > 0 && (
                  <GruppoScelta legenda="Da dove prendi il modulo?" obbligatorio valore={stato.origine}
                                cambia={(origine) => cambia({ origine: origine as StatoModulo['origine'] })}
                                errore={invio.errori.origine}
                                opzioni={[
                                  { valore: 'nuovo', etichetta: 'Carico il file adesso' },
                                  { valore: 'esistente', etichetta: 'È già tra i documenti della scheda' },
                                ]} />
                )}
                {stato.origine === 'nuovo' && (
                  <SceltaScansione file={file} cambia={setFile} errore={invio.errori.documento}
                                   bloccata={invio.inCorso} obbligatoria />
                )}
                {stato.origine === 'esistente' && (
                  <Scelta etichetta="Documento della scheda" obbligatorio vuota="Scegli…" valore={stato.documento_esistente}
                          cambia={(documento_esistente) => cambia({ documento_esistente })}
                          errore={invio.errori.documento_esistente ?? invio.errori.documento}
                          aiuto={`Valgono i documenti di tipo ${ammessi.map((t) => `«${t.nome}»`).join(' o ')}.`}
                          opzioni={esistenti.map((d) => ({
                            valore: d.id,
                            etichetta: `${d.nome} · ${nomeTipo(d.tipo)} · caricato il ${giornoItaliano(d.caricato_il)}`
                              + (d.antivirus === 'in_attesa' ? ' · in verifica antivirus' : ''),
                          }))} />
                )}
              </>
              )}
      </form>
    </Modale>
  )
}

// --- completamento della firma di un terzo: percorso guidato ------------------------------------

/**
 * Termine della firma nel percorso guidato: entro quando completarla o, se e' passato, che ne viene
 * avvisata anche la Direzione.
 */
function TermineCompletamento({ firma }: { firma: FirmaDaCompletare }) {
  const giorno = giornoItaliano(firma.completare_entro)
  return firma.termine_scaduto
    ? (
      <Avviso tipo="attenzione">
        Il termine per completare questa firma è scaduto il {giorno}: ne viene avvisata anche la Direzione. I consensi
        restano registrati; completala appena hai il documento.
      </Avviso>
      )
    : <Avviso tipo="attenzione">Da completare entro il {giorno}.</Avviso>
}

/** Documento della scheda come lo elenca GET /persone/:id/documenti: quello che serve per sceglierlo. */
type DocumentoScheda = {
  id: string; nome: string; tipo: string; antivirus: string; origine: string
  caricato_il: string; data_documento: string | null
}

/**
 * Percorso guidato che completa la firma di chi ha firmato al posto della persona: 1. il documento
 * (un file da caricare, oppure uno gia' tra i documenti della scheda, del tipo giusto); 2. il tipo,
 * con data e scadenza del file nuovo, e il nome di chi ha firmato se la firma non lo ha; 3. la
 * conferma, che carica il file e lo collega a tutta la firma (PUT .../consensi/:consenso/giustificativo).
 *
 * Le verifiche di ogni passo sono quelle di regole-consensi.ts; quelle dell'API riportano al passo
 * del campo. Il file caricato e non collegato (per esempio per un errore sul nome) non si ricarica.
 */
function PercorsoCompletamento({ personaId, firma, chiudi, conclusa }: {
  personaId: string; firma: FirmaDaCompletare; chiudi: () => void; conclusa: (messaggio: string) => Promise<void>
}) {
  const oggi = oggiLocale()
  const { tipi, errore: erroreTipi } = useTipiGiustificativo(true)
  const ammessi = tipiGiustificativo(tipi ?? [], firma.firmatario)
  const [esistenti, setEsistenti] = useState<DocumentoScheda[] | null>(null)
  const [stato, setStato] = useState<StatoCompletamento | null>(null)
  const [passo, setPasso] = useState(0)
  const [file, setFile] = useState<File | null>(null)
  const [caricato, setCaricato] = useState<{ file: File; tipo: string; id: string } | null>(null)
  const invio = useInvioConsensi()
  const titoloPasso = useRef<HTMLHeadingElement>(null)
  const primoDisegno = useRef(true)

  // Documenti della scheda gia' adatti alla firma: non cancellati, non infetti, del tipo che prova il
  // potere di quel firmatario. Il decreto caricato dalla linguetta Documenti si usa senza ricaricarlo.
  useEffect(() => {
    if (!tipi) return
    let vivo = true
    const adatti = tipiGiustificativo(tipi, firma.firmatario)
    Promise.all(adatti.map((t) => api.get<DocumentoScheda[]>(
      `/persone/${personaId}/documenti?tipo=${encodeURIComponent(t.codice)}&limite=50`)))
      .then((liste) => liste.flat().filter((d) => d.antivirus !== 'infetto' && d.origine !== 'quarantena'))
      .catch(() => [] as DocumentoScheda[])
      .then((trovati) => {
        if (!vivo) return
        setEsistenti(trovati)
        setStato((s) => s ?? completamentoNuovo(adatti, trovati.length > 0))
      })
    return () => { vivo = false }
  }, [tipi, personaId, firma.firmatario])

  // Cambiando passo il fuoco va sul titolo del passo, cosi' chi usa un lettore di schermo sente dove si trova.
  useEffect(() => {
    if (primoDisegno.current) { primoDisegno.current = false; return }
    titoloPasso.current?.focus()
  }, [passo])

  const cambia = (p: Partial<StatoCompletamento>) => setStato((s) => (s ? { ...s, ...p } : s))
  const scelto = esistenti?.find((d) => d.id === stato?.documento_esistente) ?? null
  const nomeTipo = (codice: string) => tipi?.find((t) => t.codice === codice)?.nome ?? 'documento'
  const tipoFinale = stato?.origine === 'esistente' ? scelto?.tipo ?? '' : stato?.tipo ?? ''

  function avanti() {
    if (!stato) return
    const errori = verificaPassoCompletamento(passo, stato, {
      file: !!file, tipiAmmessi: ammessi, nomeMancante: firma.nome_mancante, oggi,
    })
    if (!nessunErrore(errori)) { invio.segnala(errori); return }
    invio.azzera()
    if (passo < PASSI_COMPLETAMENTO.length - 1) setPasso(passo + 1)
    else void conferma(stato)
  }

  function indietro() {
    invio.azzera()
    setPasso((p) => Math.max(0, p - 1))
  }

  async function conferma(s: StatoCompletamento) {
    invio.inizia()
    try {
      let documento = s.origine === 'esistente' ? s.documento_esistente : null
      if (s.origine === 'nuovo' && file) {
        if (caricato?.file === file && caricato.tipo === s.tipo) documento = caricato.id
        else {
          documento = await caricaDocumento(personaId, {
            nome: file.name, tipo: s.tipo, dati: file,
            data_documento: s.data_documento || null, scadenza: s.scadenza || null,
          })
          setCaricato({ file, tipo: s.tipo, id: documento })
        }
      }
      if (!documento) { setPasso(0); invio.segnala({ giustificativo: 'Scegliere il file del documento.' }); return }
      await api.put(`/persone/${personaId}/consensi/${firma.consenso_id}/giustificativo`,
        corpoGiustificativo(s, documento, firma.nome_mancante))
      await conclusa('Firma completata: il documento sul potere di firma è collegato.')
    } catch (e) {
      // Completata da un altro operatore nel frattempo: non c'e' niente da correggere, si torna alla scheda.
      if (e instanceof ErroreApi && e.codice === 'GIUSTIFICATIVO_GIA_COLLEGATO') {
        await conclusa('La firma era già stata completata da un altro operatore.')
        return
      }
      const torna = e instanceof ErroreApi ? passoDellErrore(erroriDaApi(e)) : null
      if (torna !== null) setPasso(torna)
      invio.fallito(e)
    } finally { invio.setInCorso(false) }
  }

  const ultimo = passo === PASSI_COMPLETAMENTO.length - 1
  const pronto = !!stato && !!esistenti
  const comandi = (
    <>
      {passo === 0
        ? <Bottone variante="secondario" onClick={chiudi} disabled={invio.inCorso}>Annulla</Bottone>
        : <Bottone variante="secondario" onClick={indietro} disabled={invio.inCorso}>Indietro</Bottone>}
      {pronto && ammessi.length > 0 && (
        <Bottone variante="primario" inCorso={invio.inCorso} onClick={avanti}>
          {!ultimo ? 'Avanti' : stato.origine === 'nuovo' ? 'Carica e collega' : 'Collega'}
        </Bottone>
      )}
    </>
  )

  return (
    <Modale titolo="Completa la firma" chiudi={chiudi} larga azioni={comandi}>
      <form ref={invio.radice} noValidate onSubmit={(ev) => { ev.preventDefault(); avanti() }}>
        <ol className="persona-passi" aria-label="Passi del completamento">
          {PASSI_COMPLETAMENTO.map((nome, i) => (
            <li key={nome} aria-current={i === passo ? 'step' : undefined}
                className={i < passo ? 'persona-passo-fatto' : undefined}>
              {nome}
            </li>
          ))}
        </ol>
        <h3 ref={titoloPasso} tabIndex={-1} className="persona-passo-titolo">
          Passo {passo + 1} di {PASSI_COMPLETAMENTO.length}: {PASSI_COMPLETAMENTO[passo].toLowerCase()}
        </h3>
        {invio.errore && <Avviso tipo="errore">{invio.errore}</Avviso>}

        {erroreTipi
          ? <Avviso tipo="errore">I tipi di documento non sono disponibili in questo momento. Riprova tra poco.</Avviso>
          : !pronto
            ? <Attesa testo="Preparazione…" />
            : !ammessi.length
              ? (
                <Avviso tipo="errore">
                  Per questa firma il catalogo non prevede un tipo di documento: segnalalo all’amministratore.
                </Avviso>
                )
              : (
                <>
                  {passo === 0 && (
                    <>
                      <p className="persona-nota-sezione">
                        {descriviFirma(firma)}. {spiegazioneGiustificativo(firma.firmatario)}
                        {firma.giustificativo_da_sostituire
                          && ' Il documento collegato in precedenza è stato cancellato o non ha superato il controllo antivirus.'}
                      </p>
                      <TermineCompletamento firma={firma} />
                      {esistenti.length > 0 && (
                        <GruppoScelta legenda="Da dove prendi il documento?" obbligatorio valore={stato.origine}
                                      cambia={(origine) => cambia({ origine: origine as StatoCompletamento['origine'] })}
                                      errore={invio.errori.origine}
                                      opzioni={[
                                        { valore: 'nuovo', etichetta: 'Carico il file adesso' },
                                        { valore: 'esistente', etichetta: 'È già tra i documenti della scheda' },
                                      ]} />
                      )}
                      {stato.origine === 'nuovo' && (
                        <SceltaFile etichetta="File del documento" comando="Scegli il file" file={file} cambia={setFile}
                                    errore={invio.errori.giustificativo} bloccata={invio.inCorso} obbligatoria
                                    nota={NOTA_FILE} />
                      )}
                      {stato.origine === 'esistente' && (
                        <Scelta etichetta="Documento della scheda" obbligatorio vuota="Scegli…" valore={stato.documento_esistente}
                                cambia={(documento_esistente) => cambia({ documento_esistente })}
                                errore={invio.errori.documento_esistente ?? invio.errori.giustificativo}
                                opzioni={esistenti.map((d) => ({
                                  valore: d.id,
                                  etichetta: `${d.nome} · ${nomeTipo(d.tipo)} · caricato il ${giornoItaliano(d.caricato_il)}`,
                                }))} />
                      )}
                    </>
                  )}

                  {passo === 1 && (
                    <>
                      {stato.origine === 'nuovo'
                        ? (
                          <>
                            {ammessi.length === 1
                              ? (
                                <DatoDerivato etichetta="Tipo di documento" valore={ammessi[0].nome}
                                              aiuto={`È il documento che prova il potere di firma ${firmaDi(firma.firmatario)}.`} />
                                )
                              : (
                                <GruppoScelta legenda="Che documento è?" obbligatorio valore={stato.tipo}
                                              cambia={(tipo) => cambia({ tipo })} errore={invio.errori.tipo}
                                              opzioni={ammessi.map((t) => ({ valore: t.codice, etichetta: t.nome }))} />
                                )}
                            <div className="griglia-modulo">
                              <Campo tipo="date" etichetta="Data del documento" valore={stato.data_documento} max={oggi}
                                     cambia={(data_documento) => cambia({ data_documento })} errore={invio.errori.data_documento}
                                     aiuto="Facoltativa: la data scritta sul documento." />
                              <Campo tipo="date" etichetta="Valido fino al" valore={stato.scadenza}
                                     min={stato.data_documento || undefined}
                                     cambia={(scadenza) => cambia({ scadenza })} errore={invio.errori.scadenza}
                                     aiuto="Facoltativa: se il documento scade, trenta giorni prima arriva un avviso." />
                            </div>
                          </>
                          )
                        : (
                          <DatoDerivato etichetta="Tipo di documento" valore={scelto ? nomeTipo(scelto.tipo) : ''}
                                        aiuto="Tipo e date restano quelli registrati sul documento." />
                          )}
                      {firma.nome_mancante && (
                        <Campo etichetta="Nome e cognome di chi ha firmato" valore={stato.firmatario_nome} obbligatorio
                               maxLength={120} autoComplete="off" errore={invio.errori.firmatario_nome}
                               cambia={(firmatario_nome) => cambia({ firmatario_nome })}
                               aiuto="Con questa firma il nome non era stato registrato: indicalo come risulta dal documento." />
                      )}
                    </>
                  )}

                  {passo === 2 && (
                    <>
                      <p className="persona-nota-sezione">
                        Controlla e conferma. Il documento si collega a questa firma
                        {firma.dichiarazioni.length > 1 ? ', per tutte le dichiarazioni del modulo,' : ''} e la firma
                        risulta completa.
                      </p>
                      <dl className="persona-riepilogo">
                        <Dato nome="Firma">{descriviFirma(firma)}</Dato>
                        <Dato nome="Termine">{maiuscola(termineFirma(firma))}</Dato>
                        {firma.nome_mancante && <Dato nome="Chi ha firmato">{stato.firmatario_nome.trim()}</Dato>}
                        <Dato nome="Documento">
                          {stato.origine === 'nuovo' ? file?.name : scelto?.nome}
                          {stato.origine === 'esistente' && <span className="piccolo tenue"> · già nella scheda</span>}
                        </Dato>
                        <Dato nome="Tipo">{nomeTipo(tipoFinale)}</Dato>
                        {stato.origine === 'nuovo' && stato.data_documento && (
                          <Dato nome="Data del documento">{giornoItaliano(stato.data_documento)}</Dato>
                        )}
                        {stato.origine === 'nuovo' && stato.scadenza && (
                          <Dato nome="Valido fino al">{giornoItaliano(stato.scadenza)}</Dato>
                        )}
                      </dl>
                      {stato.origine === 'nuovo' && (
                        <p className="piccolo tenue">
                          Il file si carica cifrato tra i documenti della scheda e si può scaricare dopo il controllo
                          antivirus.
                        </p>
                      )}
                    </>
                  )}
                </>
                )}
      </form>
    </Modale>
  )
}
